import {useCensusEntriesListHook} from "./UseCensusEntriesListHook";
import {useEquipmentWithFilterListHook} from "./UseEquipmentWithFilterListHook";
import {uuid, VeboxModels} from "../../../../../types/veboxModels";
import useSWR from "swr";
import {RestDataLoader} from "../../../../../dashboard/widgets/unilever/RestDataLoader";
import {getBaseUrl} from "../controllers/CensusCoolerController";
import {CensusProgressInfo} from "../models/CensusProgressInfo";
import {CensusEnums} from "../models/CensusEnums";
import {useCensusEntriesByInventoryStatusHook} from "./UseCensusEntriesByInventoryStatusHook";
import {JSONCensusSuggestedActionsAnalysisSummary} from "../models/JSONCensusSuggestedActionsAnalysisSummary";
import {JSONList} from "../models/JSONList";
import {JSONLocationSuggestion} from "../models/JSONLocationSuggestion";

export const useCensusEntriesList = useCensusEntriesListHook;
export const useEquipmentWithFilterList = useEquipmentWithFilterListHook;
export const useCensusEntriesByInventoryStatus =
    useCensusEntriesByInventoryStatusHook;

export function useEquipmentGuidsWithFilterList(
    filterGuid?: uuid,
    shouldFetch?: boolean,
) {
    const configuration = {
        url: `equipment/guids`,
        params: {filterGuid},
    };
    return useSWR<uuid[], Error>(
        shouldFetch ? configuration : null,
        RestDataLoader.fetchData,
    );
}

export function useCensusProgressInfo(censusGuid: uuid, shouldFetch?: boolean) {
    const configuration = {
        url: `${getBaseUrl(censusGuid)}updatingProgressInfo`,
        params: {},
    };
    return useSWR<CensusProgressInfo, Error>(
        shouldFetch ? configuration : null,
        RestDataLoader.fetchData,
    );
}

export function useCensusEnums(shouldFetch?: boolean) {
    const configuration = {
        url: `${getBaseUrl()}censusEnums`,
        params: {},
    };
    return useSWR<CensusEnums, Error>(
        shouldFetch ? configuration : null,
        RestDataLoader.fetchData,
    );
}

export function useCensusSuggestedActionsAnalysisSummary(
    censusGuid: uuid,
    shouldFetch: boolean,
) {
    const configuration = {
        url: `${getBaseUrl(censusGuid)}suggestedActionsAnalysisSummary`,
        params: {},
    };
    return useSWR<JSONCensusSuggestedActionsAnalysisSummary, Error>(
        shouldFetch ? configuration : null,
        RestDataLoader.fetchData,
    );
}

export function useEquipmentModel(shouldFetch: boolean, partialName: string) {
    const params = {
        partialName: partialName,
        categories: ["COOLER", "FREEZER"],
        withCustomInfo: true,
    };

    const configuration = {
        url: `equipment/models${RestDataLoader.objToUrlParams(Object.keys(params), params)}`,
        params: {},
    };
    return useSWR<JSONList<VeboxModels.EquipmentModel>, Error>(
        shouldFetch ? configuration : null,
        RestDataLoader.fetchData,
    );
}

export function useLocationsSuggestions(shouldFetch: boolean, name: string) {
    const params = {
        name: name,
        limit: 20,
    };

    const configuration = {
        url: `locations/suggestions`,
        params: params,
    };
    return useSWR<JSONLocationSuggestion[], Error>(
        shouldFetch ? configuration : null,
        RestDataLoader.fetchData,
    );
}

export function useDeviceModels(shouldFetch: boolean) {
    const params = {
        types: ["OCTO", "VBCTK", "SOLO"],
        devicesBelongsToCompany: true,
    };

    const configuration = {
        url: `devices/models${RestDataLoader.objToUrlParams(Object.keys(params), params)}`,
        params: {},
    };
    return useSWR<JSONList<VeboxModels.DeviceModel>, Error>(
        shouldFetch ? configuration : null,
        RestDataLoader.fetchData,
    );
}

export function useVisibleHierarchy(shouldFetch: boolean) {
    const configuration = {
        url: `users/current/hierarchies/tree`,
        params: {},
    };
    return useSWR<JSONList<VeboxModels.HierarchiesTree>, Error>(
        shouldFetch ? configuration : null,
        RestDataLoader.fetchData,
    );
}
